import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchCurrentSession } from '../lib/session';
import type { User, LoginCredentials, RegisterData } from '../types';

// ---------------------------
// Context shape
// ---------------------------

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<User>;
  loginWithGoogle: (credential: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ---------------------------
// Hook
// ---------------------------

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return context;
};

// ---------------------------
// Provider
// ---------------------------

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async (): Promise<User | null> => {
    try {
      const data = await fetchCurrentSession();
      if (data && data.user) {
        setUser(data.user);
        return data.user;
      }
      setUser(null);
      return null;
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setUser(null);
        return null;
      }
      // On network errors or transient timeouts, preserve existing authenticated session
      console.warn("Erreur transitoire lors du rafraîchissement utilisateur:", err);
      return user;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await refreshUser();
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    const { api } = await import('../lib/api');
    const data = await api.auth.login(credentials);
    setUser(data.user);
    return data.user;
  };

  const register = async (data: RegisterData): Promise<User> => {
    const { api } = await import('../lib/api');
    const res = await api.auth.register(data);
    setUser(res.user);
    return res.user;
  };

  const loginWithGoogle = async (credential: string): Promise<User> => {
    const { api } = await import('../lib/api');
    const data = await api.auth.google(credential);
    setUser(data.user);
    return data.user;
  };

  const logout = async (): Promise<void> => {
    const { api } = await import('../lib/api');
    await api.auth.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
